import React, { useState, useEffect } from 'react';
import {
  Button,
  DatePicker,
  Input,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { batchApprovalApi, PendingItem } from '../../../services/batchApprovalApi';
import { useBatchApproval, buildRowKey } from '../hooks/useBatchApproval';
import {
  BatchAcceptanceBridge,
  pw2502f1AcceptanceManifest,
  pw2503f1AcceptanceManifest,
  pw2504f1AcceptanceManifest,
  pw250af1AcceptanceManifest,
} from '../../../features/acceptance';
import BatchResultModal from './BatchResultModal';
import DocumentDetailModal from './DocumentDetailModal';

const { RangePicker } = DatePicker;
const { Text } = Typography;
const acceptanceByModule = {
  pw2502: pw2502f1AcceptanceManifest,
  pw2503: pw2503f1AcceptanceManifest,
  pw2504: pw2504f1AcceptanceManifest,
  pw250a: pw250af1AcceptanceManifest,
};
const acceptanceModuleByFormId = {
  pw2502f1: 'pw2502',
  pw2503f1: 'pw2503',
  pw2504f1: 'pw2504',
  pw250af1: 'pw250a',
} as const;

// ============================================================
// Status helper
// ============================================================

function statusTag(status: string): React.ReactNode {
  switch (status) {
    case 'S':
    case 'D': // FC120（交運單）法定送審值為 'D'
	case 'N': // CHAIN-25 pw2502/3/4/a 法定待核准值
      return <Tag color="orange">待審核</Tag>;
    case 'P':
      return <Tag color="green">已核准</Tag>;
    case 'R':
      return <Tag color="red">已退回</Tag>;
    default:
      return <Tag>{status || '—'}</Tag>;
  }
}

// ============================================================
// Component
// ============================================================

interface ApprovalTabProps {
  acceptanceFormId?: string;
}

const ApprovalTab: React.FC<ApprovalTabProps> = ({ acceptanceFormId }) => {
  const {
    loading,
    operating,
    items,
    total,
    modules,
    selectedKeys,
    filter,
    resultData,
    resultVisible,
    setFilter,
    setSelectedKeys,
    fetchPending,
    batchApprove,
    batchReject,
    closeResult,
  } = useBatchApproval();

  const [detailItem, setDetailItem] = useState<PendingItem | null>(null);
  const [detailVisible, setDetailVisible] = useState(false);
  const [approveComment, setApproveComment] = useState('');
  const [rejectComment, setRejectComment] = useState('');
  const requestedAcceptanceModule = acceptanceFormId
    ? acceptanceModuleByFormId[acceptanceFormId as keyof typeof acceptanceModuleByFormId]
    : undefined;
  const acceptanceModule = (
    requestedAcceptanceModule || filter.modId || items[0]?.modId || 'pw2502'
  ) as keyof typeof acceptanceByModule;
  const acceptanceManifest = acceptanceByModule[acceptanceModule] || pw2502f1AcceptanceManifest;
	const selectedApprovalOnly = selectedKeys.some((key) => {
		const modId = key.split('::')[0];
		return modules.some((m) => m.modId === modId && m.approvalOnly);
	});

  // Auto-load on mount
  useEffect(() => {
    fetchPending();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ============================================================
  // Table columns
  // ============================================================

  const columns: ColumnsType<PendingItem> = [
    {
      title: '模組名稱',
      dataIndex: 'modName',
      key: 'modName',
      width: 140,
      ellipsis: true,
    },
    {
      title: '單據號碼',
      dataIndex: 'recordNo',
      key: 'recordNo',
      width: 130,
      render: (text: string) => <Text code>{text}</Text>,
    },
    {
      title: '日期',
      dataIndex: 'recordDate',
      key: 'recordDate',
      width: 110,
      render: (date: string | null) =>
        date ? new Date(date).toLocaleDateString('zh-TW') : '—',
    },
    {
      title: '建立人',
      dataIndex: 'creator',
      key: 'creator',
      width: 100,
    },
    {
      title: '狀態',
      dataIndex: 'status',
      key: 'status',
      width: 90,
      align: 'center',
      render: statusTag,
    },
  ];

  // ============================================================
  // Row selection
  // ============================================================

  const rowSelection = {
    selectedRowKeys: selectedKeys,
    onChange: (keys: React.Key[]) => setSelectedKeys(keys as string[]),
  };

  // ============================================================
  // Filter handlers
  // ============================================================

  const handleModuleChange = (value: string) => {
    setFilter({ modId: value });
  };

  const handleDateChange = (_: unknown, dateStrings: [string, string]) => {
    setFilter({ dateFrom: dateStrings[0], dateTo: dateStrings[1] });
  };

  const handleSearch = () => {
    fetchPending();
  };

  const handlePageChange = (page: number, pageSize: number) => {
    setFilter({ page, size: pageSize });
    // fetchPending will be triggered by the filter change effect below
  };

  // Refetch when filter changes (module, date, page)
  useEffect(() => {
    fetchPending();
  }, [filter.modId, filter.dateFrom, filter.dateTo, filter.page]); // eslint-disable-line react-hooks/exhaustive-deps

  // ============================================================
  // Row click
  // ============================================================

  const handleRowClick = (record: PendingItem) => {
    setDetailItem(record);
    setDetailVisible(true);
  };

  // ============================================================
  // Render
  // ============================================================

  return (
    <div>
      {/* Filter Bar */}
      <div className="batch-filter-bar">
        <Space wrap>
          <Select
            data-contract-field="filter_module"
            value={filter.modId || undefined}
            onChange={handleModuleChange}
            placeholder="全部模組"
            allowClear
            style={{ width: 180 }}
            options={modules.map((m) => ({
              value: m.modId,
              label: m.displayName,
            }))}
          />
          <RangePicker
            data-contract-field="filter_date_range"
            value={[
              filter.dateFrom ? dayjs(filter.dateFrom) : null,
              filter.dateTo ? dayjs(filter.dateTo) : null,
            ]}
            onChange={handleDateChange as any}
            format="YYYY-MM-DD"
          />
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={handleSearch}
            loading={loading}
          >
            查詢
          </Button>
        </Space>
      </div>

      {/* Data Table */}
      <Table<PendingItem>
        rowKey={buildRowKey}
        columns={columns}
        dataSource={items}
        loading={loading}
        rowSelection={rowSelection}
        size="small"
        pagination={{
          current: filter.page,
          pageSize: filter.size,
          total,
          onChange: handlePageChange,
          showTotal: (t) => `共 ${t} 筆`,
          showSizeChanger: true,
          pageSizeOptions: ['20', '50', '100'],
        }}
        scroll={{ x: 600 }}
        locale={{ emptyText: '無待審核資料' }}
        onRow={(record) => ({
          onClick: () => handleRowClick(record),
          style: { cursor: 'pointer' },
        })}
      />

      {/* Bottom action bar */}
      <div className="batch-action-bar">
        <Space>
          <Text type="secondary">
            已選 <Text strong>{selectedKeys.length}</Text> 筆
          </Text>
        </Space>
        <Space>
          <Popconfirm
            title={`確認退回 ${selectedKeys.length} 筆文件？`}
            description={
              <Input.TextArea
                rows={2}
                placeholder="退回原因（選填）"
                value={rejectComment}
                onChange={(e) => setRejectComment(e.target.value)}
              />
            }
            onConfirm={() => {
              batchReject(rejectComment);
              setRejectComment('');
            }}
            okText="確認退回"
            cancelText="取消"
            okButtonProps={{ danger: true }}
			disabled={selectedKeys.length === 0 || selectedApprovalOnly}
          >
            <Button
              danger
              icon={<CloseCircleOutlined />}
              loading={operating}
			  disabled={selectedKeys.length === 0 || selectedApprovalOnly}
            >
              全部退回
            </Button>
          </Popconfirm>
		  {selectedApprovalOnly && (
			<Text type="secondary">此核准佇列依 FoxPro 僅支援核准，不支援退回</Text>
		  )}
          <Popconfirm
            title={`確認核准 ${selectedKeys.length} 筆文件？`}
            description={
              <Input.TextArea
                rows={2}
                placeholder="核准備註（選填）"
                value={approveComment}
                onChange={(e) => setApproveComment(e.target.value)}
              />
            }
            onConfirm={() => {
              batchApprove(approveComment);
              setApproveComment('');
            }}
            okText="確認核准"
            cancelText="取消"
            disabled={selectedKeys.length === 0}
          >
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              loading={operating}
              disabled={selectedKeys.length === 0}
            >
              全部核准
            </Button>
          </Popconfirm>
        </Space>
      </div>

      {/* Result Modal */}
      <BatchResultModal
        visible={resultVisible}
        data={resultData}
        onClose={closeResult}
        title="批次核准/退回結果"
      />

      {/* Document Detail Modal */}
      <DocumentDetailModal
        visible={detailVisible}
        item={detailItem}
        onClose={() => setDetailVisible(false)}
      />
      <BatchAcceptanceBridge
        manifest={acceptanceManifest}
        records={items.filter((item) => item.modId === acceptanceModule)}
        selectedKeys={selectedKeys}
        keyOf={buildRowKey}
        queryExact={async (key) => {
          const [modId, recordNo] = key.split('::');
          const response = await batchApprovalApi.getPending({ ...filter, modId, page: 1, size: 100 });
          return response.data.find((item) => item.recordNo === recordNo) || null;
        }}
        lastApiResponse={items}
        loading={loading || operating}
        onAutoFill={() => {
          const first = items.find((item) => item.modId === acceptanceModule);
          if (first) setSelectedKeys([buildRowKey(first)]);
        }}
        autoFillDisabled={!items.some((item) => item.modId === acceptanceModule)}
        screenDraft={{ filter, approveComment, rejectComment }}
      />
    </div>
  );
};

export default ApprovalTab;
